import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import "../styles/Landing.css";
import heroImage from "../assets/hero-full.png";

// ================= HOOK OUTSIDE COMPONENT =================
function useCountFlicker(finalValue, duration = 1200, startAnimation = false) {
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (!startAnimation) return;
    let start = 0;
    let startTime = performance.now();

    function animate(time) {
      const progress = Math.min((time - startTime) / duration, 1);

      if (progress < 0.4) {
        setValue(Math.floor(Math.random() * finalValue));
      } else {
        const eased = start + (finalValue - start) * progress;
        setValue(Math.floor(eased));
      }

      if (progress < 1) requestAnimationFrame(animate);
      else setValue(finalValue);
    }

    requestAnimationFrame(animate);
  }, [finalValue, duration, startAnimation]);

  return value;
}

// =========================================================

function Landing() {
  const [navOpen, setNavOpen] = useState(false);
  const [active, setActive] = useState("home");
  const [statsVisible, setStatsVisible] = useState(false);

  // animated values
  const projCount = useCountFlicker(20000, 1200, statsVisible);
  const vendorCount = useCountFlicker(8000, 1200, statsVisible);
  const payoutCount = useCountFlicker(85000000, 1200, statsVisible);

  // testimonials
  const testimonials = [
    { name: "Aarav Patel", role: "Freelance Data Analyst", text: "Orvion has been a game-changer. I found consistent data entry projects within days.", rating: 5 },
    { name: "Priya Sharma", role: "Content Moderator", text: "The payout system is incredibly fast. I love the transparency and support.", rating: 5 },
    { name: "Rohan Gupta", role: "AI Trainer", text: "Best platform for Indian freelancers. The projects are diverse and well-paid.", rating: 5 },
    { name: "Sneha Reddy", role: "Voice Artist", text: "I was skeptical at first, but now I earn a steady income recording voiceovers.", rating: 5 },
    { name: "Vikram Singh", role: "Transcriptionist", text: "Simple interface, great projects. Highly recommended for anyone looking for remote work.", rating: 5 },
    { name: "Ananya Das", role: "Translator", text: "Finally a platform that values skills. The verification process was smooth.", rating: 5 },
    { name: "Karthik Nair", role: "Image Annotator", text: "I've tried many sites, but Orvion is by far the most reliable for payments.", rating: 5 },
    { name: "Meera Iyer", role: "Quality Analyst", text: "Great community and even better opportunities. Loving my journey here.", rating: 5 },
  ];

  // NAV ACTIVE STATE
  useEffect(() => {
    const sections = Array.from(document.querySelectorAll("section[data-section]"));
    const onScroll = () => {
      const scrollPos = window.scrollY + 120;
      for (let sec of sections) {
        const top = sec.offsetTop;
        const bottom = top + sec.offsetHeight;
        const id = sec.getAttribute("id");
        if (scrollPos >= top && scrollPos < bottom) {
          setActive(id);
          break;
        }
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => (e) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (!el) return;

    const top = el.getBoundingClientRect().top + window.scrollY - 80;
    window.scrollTo({ top, behavior: "smooth" });
    setNavOpen(false);
  };

  // STATS OBSERVER
  useEffect(() => {
    const statsSection = document.getElementById("stats");
    if (!statsSection) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setStatsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );

    observer.observe(statsSection);
    return () => observer.disconnect();
  }, []);

  // ANIMATION OBSERVER
  useEffect(() => {
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("in");
        }
      });
    }, { threshold: 0.15 });

    const elements = document.querySelectorAll('.reveal');
    elements.forEach((n) => io.observe(n));

    return () => io.disconnect();
  }, []);

  // Real Dummy Projects Data
  const dummyProjects = [
    { title: "AI Content Moderation", tags: "Trust & Safety • Remote", price: "₹12,000", days: "3" },
    { title: "E-commerce Product Tagging", tags: "Data Entry • Flexible", price: "₹5,000", days: "2" },
    { title: "Voice Recording for AI", tags: "Audio Collection • 1hr", price: "₹8,500", days: "1" },
    { title: "Medical Image Annotation", tags: "Healthcare AI • Expert", price: "₹15,000", days: "5" },
    { title: "Sentiment Analysis Dataset", tags: "Text Processing • Bulk", price: "₹9,000", days: "4" },
    { title: "Video Transcription", tags: "Media Services • English", price: "₹6,500", days: "2" },
  ];

  return (
    <div className="landing-root">

      {/* HEADER */}
      <header className={`landing-header ${navOpen ? "open" : ""}`}>
        <div className="landing-nav-inner">
          <div className="landing-brand" onClick={scrollTo("home")}>
            <img src="/orvion-logo.png" alt="Orvion" className="landing-logo-img" />
            <span>Orvion</span>
          </div>

          <nav className={`landing-nav-links ${navOpen ? "show" : ""}`}>
            <a href="#home" className={active === "home" ? "active" : ""} onClick={scrollTo("home")}>Home</a>
            <a href="#features" className={active === "features" ? "active" : ""} onClick={scrollTo("features")}>Features</a>
            <a href="#how" className={active === "how" ? "active" : ""} onClick={scrollTo("how")}>How it works</a>
            <a href="#projects" className={active === "projects" ? "active" : ""} onClick={scrollTo("projects")}>Projects</a>
            <a href="#stats" className={active === "stats" ? "active" : ""} onClick={scrollTo("stats")}>Stats</a>
            <a href="#pricing" className={active === "pricing" ? "active" : ""} onClick={scrollTo("pricing")}>Pricing</a>
            <a href="#faq" className={active === "faq" ? "active" : ""} onClick={scrollTo("faq")}>FAQ</a>
          </nav>

          <div className="landing-nav-cta">
            <Link to="/login" className="landing-link-signin">Sign in</Link>
            <Link to="/signup">
              <button className="landing-btn-gradient">Get started</button>
            </Link>

            <button className="landing-burger" onClick={() => setNavOpen((s) => !s)}>
              <span></span><span></span><span></span>
            </button>
          </div>
        </div >
      </header >

      <main>
        {/* HERO */}
        <section
          id="home"
          data-section="home"
          className="landing-hero"
          style={{
            backgroundImage: `url(${heroImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center bottom',
            backgroundRepeat: 'no-repeat'
          }}
        >
          <div className="landing-hero-content reveal">
            <h1 className="landing-hero-title">
              <span className="blur-text" style={{ animationDelay: '0s' }}>Ready</span>{' '}
              <span className="blur-text" style={{ animationDelay: '0.2s' }}>to</span>{' '}
              <span className="blur-text highlight-text-blue" style={{ animationDelay: '0.4s' }}>Work</span>{' '}
              <span className="blur-text highlight-text-black" style={{ animationDelay: '0.6s' }}>Without</span>{' '}
              <span className="blur-text highlight-text-blue" style={{ animationDelay: '0.8s' }}>Waiting</span>
            </h1>
            <p className="landing-hero-sub">Join Orvion and experience the future of freelancing — fast, direct, and effortless.</p>

            <div className="landing-hero-actions">
              <Link to="/signup">
                <button className="landing-btn-gradient">Get Started</button>
              </Link>
            </div>

            <ul className="landing-hero-feats">
              <li style={{ justifyContent: 'center' }}>Instant work</li>
              <li>Weekly payouts</li>
              <li>24/7 support</li>
            </ul>
          </div>
        </section>

        {/* FEATURES */}
        <section id="features" data-section="features" className="landing-features">
          <h2 className="reveal">Everything you need to succeed</h2>
          <p className="landing-subtitle reveal">Powerful features designed to help vendors and freelancers manage work efficiently</p>

          <div className="landing-feature-grid">
            {[
              { icon: "⚡", title: "Instant Work", desc: "Auto-matched projects delivered to you the moment they post." },
              { icon: "🎧", title: "24/7 Help", desc: "Live support and a vendor success team available around the clock." },
              { icon: "💵", title: "Weekly Payments", desc: "Get paid fast by default — secure and transparent." }
            ].map((feat, i) => (
              <article key={i} className="landing-feature-card reveal">
                <div className="landing-icon">{feat.icon}</div>
                <h3>{feat.title}</h3>
                <p>{feat.desc}</p>
              </article>
            ))}
          </div>
        </section>

        {/* HOW IT WORKS */}
        <section id="how" data-section="how" className="landing-how">
          <h2 className="reveal">How It Works</h2>
          <p className="landing-subtitle reveal">Streamline Your AI Journey</p>

          <div className="landing-how-steps">
            {[
              { num: "1", title: "Sign Up", desc: "Create your free Orvion vendor account in minutes." },
              { num: "2", title: "Grab Projects", desc: "Browse a live marketplace — no proposals, no waiting." },
              { num: "3", title: "Start Earning", desc: "Claim a project, deliver quality work, and get paid." }
            ].map((step, i) => (
              <div key={i} className="landing-step reveal">
                <div className="landing-num">{step.num}</div>
                <h4>{step.title}</h4>
                <p>{step.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* STATS */}
        {/* STATS */}
        <section id="stats" data-section="stats" className="landing-stats">
          <div className="landing-stats-row">
            {[
              { val: `${projCount.toLocaleString()}+`, label: "Projects Posted" },
              { val: `${vendorCount.toLocaleString()}+`, label: "Active Vendors" },
              { val: `₹${(payoutCount / 10000000).toFixed(1)} Cr`, label: "Weekly Payouts" }
            ].map((stat, i) => (
              <div key={i} className="landing-stat-card reveal">
                <div className="landing-stat-num">{stat.val}</div>
                <div className="landing-stat-label">{stat.label}</div>
              </div>
            ))}
          </div>
        </section>

        {/* PROJECTS GRID */}
        <section className="landing-project-grid" id="projects" data-section="projects">
          <div className="landing-project-grid-container">
            <h2 className="landing-project-grid-heading reveal">Browse Available Projects</h2>

            <div className="landing-project-grid-row">
              {dummyProjects.map((proj, i) => (
                <div className="landing-project-card reveal" key={i}>
                  <h4>{proj.title}</h4>
                  <p style={{ fontSize: "0.9rem", color: "var(--text-tertiary)" }}>{proj.tags}</p>

                  <div className="landing-card-meta">
                    <div>{proj.price}</div>
                    <div>{proj.days} days</div>
                  </div>

                  <Link to="/signup">
                    <button className="landing-btn-card">Grab Project</button>
                  </Link>
                </div>
              ))}
            </div>

            <div className="landing-more-projects reveal">
              <Link to="/signup">
                <button className="landing-btn-more">View all projects</button>
              </Link>
            </div>
          </div>
        </section>

        {/* TESTIMONIALS */}
        <section id="testimonials" data-section="testimonials" className="landing-testimonials">
          <h2 className="reveal">Loved by vendors worldwide</h2>
          <p className="landing-subtitle reveal">Join thousands of freelancers who trust Orvion</p>

          <div className="landing-testimonials-scroll reveal">
            <div className="landing-testimonials-track">
              {[...testimonials, ...testimonials].map((t, i) => (
                <div key={i} className="landing-testimonial-card">
                  <div className="landing-test-header">
                    <div className="landing-test-avatar">{t.name[0]}</div>
                    <div>
                      <div className="landing-test-name">{t.name}</div>
                      <div className="landing-test-role">{t.role}</div>
                    </div>
                  </div>
                  <p className="landing-test-text">"{t.text}"</p>
                  <div className="landing-stars">{"★".repeat(t.rating)}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* PRICING */}
        <section id="pricing" data-section="pricing" className="pricing">
          <h2 className="reveal">Simple, transparent pricing</h2>
          <p className="landing-subtitle reveal">Choose the plan that works best for you. No hidden fees.</p>

          <div className="pricing-row">
            {[
              {
                name: "Basic",
                price: "₹499",
                feats: ["Up to 5 team members", "Up to 50 projects / month", "Weekly payout cycle", "Instant project access"],
                link: "https://pages.razorpay.com/pl_RXmPvxndGAEt9k/view"
              },
              {
                name: "Standard",
                price: "₹999",
                feats: ["Up to 100 team members", "Up to 150 projects / month", "Priority project access", "Weekly analytics report"],
                rec: true,
                link: "https://pages.razorpay.com/pl_RXmWdAyoTqKW1c/view"
              },
              {
                name: "Pro",
                price: "₹1,499",
                feats: ["Unlimited team members", "API access for automation", "VIP support", "Custom payout cycles"],
                link: "https://pages.razorpay.com/pl_RXntVQqWe1R06f/view"
              }
            ].map((plan, i) => (
              <div key={i} className={`price-card reveal ${plan.rec ? "recommended" : ""}`}>
                <div className="price-top">{plan.name}</div>
                <div className="price large">{plan.price}</div>
                <div className="price-sub">per month</div>
                <ul className="plan-bullets">
                  {plan.feats.map((f, j) => (
                    <li key={j}>{f}</li>
                  ))}
                </ul>
                <a href={plan.link} target="_blank" rel="noopener noreferrer" className="btn-gradient" style={{ display: 'inline-block', textDecoration: 'none', textAlign: 'center' }}>
                  Select
                </a>
              </div>
            ))}
          </div>
        </section>

        {/* FAQ */}
        <section id="faq" data-section="faq" className="landing-faq">
          <h2 className="reveal" style={{ textAlign: "center", marginBottom: "40px" }}>Frequently asked questions</h2>

          <div className="landing-faq-grid reveal">
            {[
              { q: "How fast can I get my first project?", a: "Depends on availability and profile match, but many vendors get a job within days." },
              { q: "When do I get paid?", a: "Weekly payouts by default — transparent and fast." },
              { q: "How does escrow work?", a: "Escrow holds funds until delivery is approved, protecting both sides." },
              { q: "Can I work on multiple projects simultaneously?", a: "Absolutely! You can take on as many projects as you can handle, provided you meet the deadlines." },
              { q: "Do I need specific software to work?", a: "Most projects are browser-based, but some specialized tasks might require specific software which will be listed in the project details." }
            ].map((item, i) => (
              <details key={i}>
                <summary>{item.q}</summary>
                <p>{item.a}</p>
              </details>
            ))}
          </div>
        </section>
      </main>

      {/* FOOTER */}
      <footer className="landing-footer">
        <div className="landing-footer-inner">
          <div className="landing-footer-left">
            <div className="landing-brand" style={{ marginBottom: "24px" }}>
              <img src="/orvion-logo.png" alt="Orvion" className="landing-logo-img" />
              <span>Orvion</span>
            </div>
            <p>The all-in-one platform for vendors and freelancers to find work, get paid, and grow their business.</p>
            <div className="landing-subscribe">
              <input type="email" placeholder="Enter your email" />
              <button className="landing-btn-sub">Subscribe</button>
            </div>
          </div>

          <div className="landing-footer-cols">
            {[
              { title: "Product", links: ["Features", "Pricing", "Projects", "FAQ"] },
              { title: "Company", links: ["About", "Reviews", "Careers"] },
              { title: "Resources", links: ["Help Center", "Guides"] },
              { title: "Legal", links: ["Privacy", "Terms"] }
            ].map((col, i) => (
              <div key={i} className="landing-col">
                <h5>{col.title}</h5>
                {col.links.map((l, j) => (
                  <a key={j} href="/">{l}</a>
                ))}
              </div>
            ))}
          </div>
        </div>

        <div className="landing-footer-bottom">
          <p>© 2025 Orvion. All rights reserved. &nbsp; support@orvion.io</p>
          <div className="landing-socials">
            <a href="https://www.linkedin.com/company/nextgenaipage/posts/?feedView=all" target="_blank" rel="noopener noreferrer" className="landing-social">
              in
            </a>
          </div>
        </div>
      </footer>

    </div >
  );
}

export default Landing;
